<template>
  <div data-test="mute-notifications-editor">
    <SList>
      <SListItem
        v-for="({ value, description, title }, index) in translationDays"
        :key="index"
        :title="title"
        :description="description"
        right-action-icon="icon-chevron-right"
        :data-cy="`settings-mute-${value.toLowerCase()}`"
        @click="muteDay(value, title)"
      />
    </SList>

    <SModal
      v-model="mute"
      :header-title="selectedTitle"
      :show-footer-gradient="false"
      data-test="mute-editor-modal"
    >
      <template v-if="mute">
        <div class="d-flex align-items-center mb-4">
          <SSelect
            v-model="startTime.value"
            :select-label="$t('settings.index.notifications.page.list.mute.from')"
            data-cy="start-time"
            data-test="mute-start-time"
            class="mb-1 w-100"
            :placeholder="$t('settings.index.notifications.page.list.mute.start_time_placeholder')"
            :disabled="isFullDay"
            :options="timeOptions"
            :searchable="false"
            @update:model-value="(newStartTime: string) => (startTime.value = newStartTime)"
          />

          <sdui-icon class="sdui-icon icon-arrow-o dark mr-4 ml-4 mt-4" />

          <SSelect
            v-model="endTime.value"
            :select-label="$t('settings.index.notifications.page.list.mute.to')"
            data-cy="end-time"
            data-test="mute-end-time"
            class="mb-1 w-100"
            :placeholder="$t('settings.index.notifications.page.list.mute.end_time_placeholder')"
            :disabled="isFullDay"
            :options="timeOptions"
            :searchable="false"
            @update:model-value="(newEndTime: string) => (endTime.value = newEndTime)"
          />
        </div>

        <!-- full day toggle -->
        <SListItem
          :title="$t('settings.index.notifications.page.list.mute.all_day_toggle')"
          :aria-pressed="isFullDay ? 'true' : 'false'"
          data-cy="all-day"
          data-test="mute-all-day-toggle"
          @click.prevent="isFullDay = !isFullDay"
        >
          <template #right-action>
            <SCheckbox
              v-model="isFullDay"
              name="isFullDay"
              :label-text="$t('settings.index.notifications.page.list.mute.all_day_toggle')"
              checkbox-type="switch"
              data-test="mute-all-day-checkbox"
            />
          </template>
        </SListItem>

        <!-- unmute & mute description -->
        <div class="text-muted small mb-3 mt-4">
          <div v-if="isFullDay" data-test="mute-description-full-day">
            {{ $t('settings.index.notifications.page.list.mute.all_day', { days: selectedTitle }) }}
          </div>

          <i18n-t v-else
                  keypath="settings.index.notifications.page.list.mute.description"
                  tag="div"
                  data-test="mute-description-window"
          >
            <template #days>
              {{ selectedTitle }}
            </template>
            <template #starts_at>
              {{ startTime.value }}
            </template>
            <template #nextday>
              {{ isToNextDay }}
            </template>
            <template #ends_at>
              {{ endTime.value }}
            </template>
          </i18n-t>
        </div>
      </template>

      <SModalPortalTo>
        <!-- unmute button -->
        <b-button
          v-if="selectedDayOptions.start_time && selectedDayOptions.end_time"
          v-btn-focus-restore-after-disable
          size="lg"
          block
          variant="primary-light"
          class="primary-light-spinner mb-3"
          :disabled="processing"
          type="submit"
          data-cy="unmute-button"
          data-test="mute-unmute-button"
          @click="processUnmute(selected)"
        >
          {{ $t('settings.index.notifications.page.list.mute.unmute') }}
        </b-button>

        <b-button
          v-btn-focus-restore-after-disable
          size="lg"
          block
          variant="primary"
          type="submit"
          :disabled="processing"
          data-cy="save-button"
          data-test="mute-save-button"
          @click="processMute(selected)"
        >
          {{ $t('global.save') }}
        </b-button>
      </SModalPortalTo>
    </SModal>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import SCheckbox from 'src/components/base/SCheckbox.vue'
import SModalPortalTo from 'src/components/base/SModal/SModalPortalTo.vue'

interface DayInterval {
  start_time: string
  end_time: string
}

type DayName = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday'
type MutedDays = Partial<Record<DayName, DayInterval>>

const DAYS: readonly DayName[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const

const props = withDefaults(
  defineProps<{
    modelValue: MutedDays
    // When provided as a boolean, the editor keeps its modal open while `processing`
    // is true and auto-closes after a Save/Unmute when `processing` flips back to false.
    // Default `null` means "not tracked" — the modal closes synchronously on Save/Unmute.
    // We use `null` (not `undefined`) because Vue 3 coerces absent boolean props to
    // `false`, which would otherwise be indistinguishable from a real `false`.
    processing?: boolean | null
  }>(),
  { processing: null },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: MutedDays): void
}>()

const { t } = useI18n()

// Reset the SList inject context so the editor's own descendants don't inherit
// "you're inside a list" from any wrapping SList in the parent. Without this,
// when the editor is rendered inside an SList (e.g. the cypress generator's
// All Properties section), the modal's Ganztägig SListItem renders as <li>
// and shows a default ::marker bullet. The editor's own day-list <SList>
// re-provides true for its direct SListItem children.
provide('isUsedInSduiList', false)

/* default times */
const endTime = reactive({ value: '18:00' })
const startTime = reactive({ value: '07:00' })

const mute = ref(false)

/* props for frontend */
const selected = ref<DayName | null>(null) // selected day
const selectedTitle = ref('') // the title of selected day in - in local language
const isFullDay = ref(false) // to mute the selected day for the whole day
// Set when a Save/Unmute fires while the parent is tracking `processing`.
// Triggers the auto-close once `processing` flips back to false.
const pendingClose = ref(false)

const timeOptions = computed(() => {
  const hours = [...Array(24).keys()]
  const time = hours.flatMap((s) => (s < 10 ? [`0${s}:00`, `0${s}:30`] : [`${s}:00`, `${s}:30`]))
  return [...time.slice(14, 48), ...time.slice(0, 14)]
})

/* day options */
const translationDays = computed(() =>
  DAYS.map((day) => ({
    title: t(`global.weekdays.${day.toLowerCase()}`, 1),
    description: describeDay(day),
    value: day,
  })),
)

const selectedDayOptions = computed<Partial<DayInterval>>(() =>
  selected.value ? (props.modelValue[selected.value] ?? {}) : {},
)

const isToNextDay = computed(() =>
  startTime.value >= endTime.value
    ? t('settings.index.notifications.page.list.mute.to_next_day')
    : '',
)

function describeDay(day: DayName): string {
  const entry = props.modelValue[day]
  if (!entry) {
    return t('settings.index.notifications.page.list.mute.deactivate')
  }
  if (entry.start_time === '00:00' && entry.end_time === '00:00') {
    return t('settings.index.notifications.page.list.mute.full_day_subtitle')
  }
  const crossesMidnight = entry.end_time <= entry.start_time
  const key = crossesMidnight
    ? 'settings.index.notifications.page.list.mute.subtitle_partial_next_day'
    : 'settings.index.notifications.page.list.mute.subtitle_partial'
  return t(key, { from: entry.start_time, to: entry.end_time })
}

function muteDay(day: DayName, title: string): void {
  selected.value = day
  mute.value = true
  selectedTitle.value = title
}

function finishEdit(): void {
  if (props.processing === null) {
    mute.value = false
    return
  }
  pendingClose.value = true
}

function processMute(day: DayName | null): void {
  if (!day) return
  const entry: DayInterval = isFullDay.value
    ? { start_time: '00:00', end_time: '00:00' }
    : { start_time: startTime.value, end_time: endTime.value }
  emit('update:modelValue', { ...props.modelValue, [day]: entry })
  finishEdit()
}

function processUnmute(day: DayName | null): void {
  if (!day) return
  const { [day]: _removed, ...rest } = props.modelValue
  emit('update:modelValue', rest)
  finishEdit()
}

watch(
  () => props.processing,
  (newVal, oldVal) => {
    if (oldVal && !newVal && pendingClose.value) {
      pendingClose.value = false
      mute.value = false
    }
  },
)

function resetToDefaults(): void {
  isFullDay.value = false
  startTime.value = '07:00'
  endTime.value = '18:00'
}

/* re-fetch watcher */
watch(mute, (isOpen) => {
  if (!isOpen) {
    resetToDefaults()
    return
  }
  /* set start & end time based on api */
  const entry = selected.value ? props.modelValue[selected.value] : undefined
  if (!entry) {
    resetToDefaults()
    return
  }
  isFullDay.value = entry.start_time === '00:00' && entry.end_time === '00:00'
  startTime.value = entry.start_time || '07:00'
  endTime.value = entry.end_time || '18:00'
})
</script>
